import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useStaffPreview } from '../hooks/useStaffPreview';

const AuthContext = createContext({
  user: null,
  loading: true,
  isAuthenticated: false,
  activeRole: null,
  availableRoles: [],
  switchRole: () => {},
  activePersona: null,
  availablePersonas: [],
  personaOptions: [],
  rolesInPersona: [],
  personaLimits: { canCreateProjects: true, restriction: null },
  switchPersona: () => false,
  // Staff-only guest preview. `previewingAs` carries the REAL operator while
  // `user` is withheld, so the banner can say who is actually signed in.
  isGuestPreview: false,
  canPreviewAsGuest: false,
  previewingAs: null,
  startGuestPreview: () => false,
  endPreview: () => {},
  logout: () => {}
});

export const AuthProvider = ({ children }) => {
  const auth = useAuth();
  const preview = useStaffPreview(auth.user);

  /**
   * Guest preview is applied HERE, at one seam, rather than inside each public
   * page.
   *
   * Every surface that distinguishes a visitor from a member does it by asking
   * this context — `isAuthenticated` for the header CTA, `user` for anything
   * personalised — so overriding both in one place makes the disguise
   * consistent by construction. Patching it per component would leave a
   * trail of half-signed-in pages, which is worse than not offering the
   * feature.
   *
   * The real operator stays reachable as `previewingAs`. Without that the
   * banner could not name who is really signed in, and "you are browsing as a
   * guest" with no way back is indistinguishable from a bug.
   */
  const value = useMemo(() => ({
    ...auth,
    ...preview,
    user: preview.isPreviewing ? null : auth.user,
    isAuthenticated: preview.isPreviewing ? false : auth.isAuthenticated,
    previewingAs: preview.isPreviewing ? auth.user : null,
    canPreviewAsGuest: preview.canPreview,
    isGuestPreview: preview.isPreviewing
  }), [
    auth.user, auth.loading, auth.activeRole, auth.availableRoles, auth.personaSettled,
    auth.activePersona, auth.availablePersonas,
    auth.refreshAuth, auth.logout, auth.switchRole, auth.switchPersona,
    preview.isPreviewing, preview.canPreview,
    preview.startGuestPreview, preview.endPreview
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
